import { z } from 'zod';
import { IsoDateTime, Uuid } from './common.js';
import {
  AnswerData,
  ClientFactData,
  KnownIssueData,
  SupplierDecisionData,
  SupportMemoryKind,
  SupportMemoryProposal,
  SupportMemoryStatus,
} from './support.js';

/**
 * Lot BE « Ce que le Cockpit sait » (contrat GELÉ le 20/10/2026, CDC « Support… ») : routes
 * `/api/support/memory*`. Rien n'entre dans la mémoire sans validation : une proposition (analyse, tutoiement
 * repéré, réponse corrigée, « Retiens ça… ») est `PROPOSED` ; « Retenir » la passe `ACTIVE`, « Non » `ARCHIVED`.
 * On archive, on ne supprime jamais. Les décisions sur les fournisseurs (`supplier_decision`) sont gérées par la
 * vue Fournisseurs (lot BG) : ici en lecture seule.
 */

/**
 * D'où vient une entrée (colonne `support_memory.origin`, ajout du lot BE) : `analysis` (proposée par l'analyse
 * d'une conversation), `tutoiement` (tu/vous repéré par l'analyse), `edit` (réponse corrigée avant l'envoi),
 * `manual` (« Retiens ça… » ou saisie), `supplier` (décision de la vue Fournisseurs).
 */
export const SupportMemoryOrigin = z.enum(['analysis', 'tutoiement', 'edit', 'manual', 'supplier']);
export type SupportMemoryOrigin = z.infer<typeof SupportMemoryOrigin>;

/** Un client touché par un problème connu (`support_issue_clients`), tableau « Clients touchés ». */
export const SupportIssueClient = z.object({
  clientId: Uuid,
  clientName: z.string(),
  conversationId: Uuid.nullable(),
  reportedAt: IsoDateTime.nullable(),
  siteUpdatedAt: IsoDateTime.nullable(),
  notifiedAt: IsoDateTime.nullable(),
});
export type SupportIssueClient = z.infer<typeof SupportIssueClient>;

const EntryBase = z.object({
  id: Uuid,
  status: SupportMemoryStatus,
  title: z.string(),
  body: z.string(),
  clientId: Uuid.nullable(),
  /** Nom du client concerné (fait sur un client), null sinon. */
  clientName: z.string().nullable(),
  origin: SupportMemoryOrigin,
  createdBy: Uuid.nullable(),
  sourceConversationId: Uuid.nullable(),
  /** « Antepostea », « Emma (Domaine des Coteaux) » : avec qui était la conversation d'origine, null sinon. */
  sourceLabel: z.string().nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

/** Une entrée de la mémoire, `data` selon `kind` (contrat). */
export const SupportMemoryEntry = z.discriminatedUnion('kind', [
  EntryBase.extend({
    kind: z.literal('known_issue'),
    data: KnownIssueData,
    /** Clients touchés, du plus récent signalement au plus ancien. */
    issueClients: z.array(SupportIssueClient),
  }),
  EntryBase.extend({ kind: z.literal('answer'), data: AnswerData }),
  EntryBase.extend({ kind: z.literal('client_fact'), data: ClientFactData }),
  EntryBase.extend({ kind: z.literal('supplier_decision'), data: SupplierDecisionData }),
]);
export type SupportMemoryEntry = z.infer<typeof SupportMemoryEntry>;

export const SupportMemoryList = z.array(SupportMemoryEntry);
export type SupportMemoryList = z.infer<typeof SupportMemoryList>;

/**
 * `GET /api/support/memory?kind=&status=` : `status` = `current` (défaut : à valider et retenues), `all`, ou un
 * statut précis.
 */
export const SupportMemoryQuery = z.object({
  kind: SupportMemoryKind.optional(),
  status: z.enum(['current', 'all', ...SupportMemoryStatus.options]).default('current'),
});
export type SupportMemoryQuery = z.input<typeof SupportMemoryQuery>;

/** Kinds modifiables ici (les décisions fournisseurs passent par la vue Fournisseurs). */
export const EditableMemoryKind = z.enum(['known_issue', 'answer', 'client_fact']);
export type EditableMemoryKind = z.infer<typeof EditableMemoryKind>;

const TITLE = z.string().trim().min(1, 'Titre obligatoire').max(200);
const BODY = z.string().trim().max(5_000);

/**
 * `POST /api/support/memory` (« Retiens ça… », saisie) : une entrée `PROPOSED` par défaut (la personne valide
 * ensuite dans « À retenir ? ») ; `ACTIVE` pour une saisie directe dans « Ce que je sais ».
 */
export const CreateSupportMemoryBody = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('known_issue'),
    title: TITLE,
    body: BODY.default(''),
    data: KnownIssueData,
    clientId: Uuid.nullable().optional(),
    sourceConversationId: Uuid.nullable().optional(),
    status: z.enum(['PROPOSED', 'ACTIVE']).default('PROPOSED'),
  }),
  z.object({
    kind: z.literal('answer'),
    title: TITLE,
    body: BODY.default(''),
    data: AnswerData,
    clientId: Uuid.nullable().optional(),
    sourceConversationId: Uuid.nullable().optional(),
    status: z.enum(['PROPOSED', 'ACTIVE']).default('PROPOSED'),
  }),
  z.object({
    kind: z.literal('client_fact'),
    title: TITLE,
    body: BODY.default(''),
    data: ClientFactData,
    clientId: Uuid.nullable().optional(),
    sourceConversationId: Uuid.nullable().optional(),
    status: z.enum(['PROPOSED', 'ACTIVE']).default('PROPOSED'),
  }),
]);
export type CreateSupportMemoryBody = z.input<typeof CreateSupportMemoryBody>;

/**
 * `PATCH /api/support/memory/:id` : activer (« Retenir »), archiver (« Non », « Archiver »), réactiver, modifier.
 * `data` remplace tout l'objet et est vérifié selon le `kind` de l'entrée.
 */
export const UpdateSupportMemoryBody = z
  .object({
    status: SupportMemoryStatus.optional(),
    title: TITLE.optional(),
    body: BODY.optional(),
    data: z.record(z.string(), z.unknown()).optional(),
    clientId: Uuid.nullable().optional(),
  })
  .refine((b) => Object.values(b).some((v) => v !== undefined), { message: 'Aucune modification demandée' });
export type UpdateSupportMemoryBody = z.input<typeof UpdateSupportMemoryBody>;

/** `POST /api/support/memory/from-edit` (contrat) : la réponse préparée a-t-elle été corrigée avant l'envoi ? */
export const SupportMemoryFromEditBody = z.object({
  conversationId: Uuid,
  sentText: z.string().trim().min(1).max(10_000),
});
export type SupportMemoryFromEditBody = z.input<typeof SupportMemoryFromEditBody>;

/**
 * Réponse de `from-edit` : `proposed` (une entrée à valider), sinon pourquoi rien : `no_prepared_reply`,
 * `small_edit` (écart trop petit), `nothing_to_learn` (Claude n'y voit rien de durable), `duplicate` (déjà su,
 * ou déjà refusé), `claude_not_configured`, `cap_reached`, `error`.
 */
export const SupportMemoryFromEditResult = z.object({
  status: z.enum([
    'proposed',
    'no_prepared_reply',
    'small_edit',
    'nothing_to_learn',
    'duplicate',
    'claude_not_configured',
    'cap_reached',
    'error',
  ]),
  entry: SupportMemoryEntry.nullable(),
});
export type SupportMemoryFromEditResult = z.infer<typeof SupportMemoryFromEditResult>;

/**
 * Lot BE, `POST /api/support/memory/suggest` (ajout) : formulaire de « Retiens ça… » prérempli par Claude à partir
 * de la conversation (rien n'est enregistré). `source` : `claude`, ou `analysis` (Claude indisponible : repris de
 * l'analyse).
 */
export const SupportMemorySuggestBody = z.object({ conversationId: Uuid });
export type SupportMemorySuggestBody = z.input<typeof SupportMemorySuggestBody>;
export const SupportMemorySuggestion = z.object({
  proposal: SupportMemoryProposal,
  clientId: Uuid.nullable(),
  source: z.enum(['claude', 'analysis']),
});
export type SupportMemorySuggestion = z.infer<typeof SupportMemorySuggestion>;
